import { useEffect, useState } from "react";
import { STATE_BY_CODE, type State } from "@/config/states";
import { trackStateSelected } from "@/lib/analytics";

const STORAGE_KEY = "olive.state.v1";
const EVENT = "olive:state-changed";

/**
 * Reads the visitor's selected state from localStorage and stays in sync
 * across components via a cross-component custom event + storage event.
 *
 * setCode(next, placement) fires a `state_selected` analytics event with a
 * placement label so we can compare which surface drives most state picks.
 */
export function useEligibility() {
  const [code, setCodeState] = useState<string>("");

  useEffect(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (v && STATE_BY_CODE[v]) setCodeState(v);
    } catch {
      /* ignore */
    }
    const onChange = (e: Event) => {
      const next = (e as CustomEvent<string>).detail ?? "";
      if (!next || STATE_BY_CODE[next]) setCodeState(next);
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      const next = e.newValue ?? "";
      if (!next || STATE_BY_CODE[next]) setCodeState(next);
    };
    window.addEventListener(EVENT, onChange as EventListener);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT, onChange as EventListener);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const setCode = (next: string, placement: string = "unknown") => {
    const previous = code;
    setCodeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent<string>(EVENT, { detail: next }));
    if (next && next !== previous) {
      trackStateSelected({
        code: next,
        name: STATE_BY_CODE[next]?.name,
        placement,
        previous,
      });
    }
  };

  const state: State | undefined = code ? STATE_BY_CODE[code] : undefined;
  return { code, state, eligible: state?.eligible, setCode };
}
